import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FirestoreAdapter } from '@shared/firestore/firestore-adapter';
import { map } from 'rxjs';

import {
  CreateStripeProductPayload,
  IStripeAccount,
  ProductNewPricePayload,
  UpdateProductInfoPayload,
} from '@models/IStripeAccount';
import { environment } from 'src/environments/environment';
import { SharedService } from './shared.service';

type StripeEndpointResponse = { success: boolean; message?: string };

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  #firebase = inject(FirestoreAdapter);
  sharedService = inject(SharedService);
  #httpClient = inject(HttpClient);
  #baseURL = `${environment.api.baseUrl}/api`;

  getStripeAccount(companyId: string) {
    return this.#firebase
      .doc<IStripeAccount>(`stripeConnect/${companyId}`)
      .snapshotChanges()
      .pipe(map((snap) => snap.payload.data()));
  }

  connectStripeAccount(value: { companyName: string; email: string }) {
    return this.#httpClient.post(
      `${environment.api.baseUrl}/api/create-account`,
      {
        companyName: value.companyName,
        email: value.email,
      },
    );
  }

  linkStripeAccount() {
    return this.#httpClient.get<{ onboardingUrl: string }>(
      `${this.#baseURL}/account-link?refresh_url=${window.location.origin + 'memberships?stripe_setup=complete'}`,
    );
  }

  createProduct(payload: CreateStripeProductPayload) {
    return this.#httpClient.post<StripeEndpointResponse>(
      `${this.#baseURL}/create-product-price`,
      payload,
    );
  }

  updateProductInfo(payload: UpdateProductInfoPayload) {
    return this.#httpClient.post<StripeEndpointResponse>(
      `${this.#baseURL}/update-product-price`,
      payload,
    );
  }

  newProductPrice(payload: ProductNewPricePayload) {
    return this.#httpClient.post<StripeEndpointResponse>(
      `${this.#baseURL}/new-price`,
      payload,
    );
  }

  connectCheckout(
    membershipId: string,
    {
      customerId,
      locationId,
      invitedBy,
      successUrl,
      cancelUrl,
      termsSignedAt,
    }: {
      customerId: string;
      locationId: string;
      invitedBy?: string;
      successUrl?: string;
      cancelUrl?: string;
      termsSignedAt?: string;
    },
  ) {
    const success_url =
      successUrl ||
      `${window.location.origin}/referrals/loyalty/card/${customerId}/loyalty-card?session-id={CHECKOUT_SESSION_ID}`;

    const cancel_url =
      cancelUrl ||
      `${window.location.origin}/referrals/loyalty/card/${customerId}/loyalty-card?cancelled=true`;

    return this.#httpClient.post<{ checkoutUrl: string }>(
      `${this.#baseURL}/checkout-connect`,
      {
        membershipId,
        customerId,
        success_url: success_url,
        cancel_url: cancel_url,
        locationId,
        invited_by: invitedBy,
        termsSignedAt,
      },
    );
  }

  cancelSubscription(subscriptionId: string, cancelledBy: string) {
    return this.#httpClient.post<StripeEndpointResponse>(
      `${this.#baseURL}/cancel-subscription`,
      { subscriptionId, cancelledBy },
    );
  }
}
